import {
  SkyDialog,
  SkyInput,
  SkyMessageBar,
  SkySwitch,
  SkyTextarea,
} from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { COPY_SOURCE_NAME_PATTERN } from "@shared/types/instancesAdmin";

/** B28: the copy-mode switch and its helper text, word for word from the pack brief. */
export const COPY_SWITCH_LABEL = "Copy from an existing vault secret";
export const COPY_SOURCE_LABEL = "Source secret name";
export const COPY_SOURCE_HINT =
  "The name of a secret already in the deployment's vault. Its value is copied into this instance's own secret and never shown.";
/**
 * B28 review r1: a name that breaks the Key Vault rule is refused HERE, in a sentence that does
 * not repeat it, so a value pasted into the name box by mistake never leaves the browser (the
 * server's "{name} is not a valid secret name." would echo it back).
 */
export const COPY_SOURCE_NAME_RULE =
  "A secret name uses only letters, digits and dashes, up to 127 characters.";

/** A name is not prose; a value pasted by mistake must not reach a spelling service either. */
function withoutSpellcheck(input: HTMLInputElement | null): void {
  if (input) input.spellcheck = false;
}

/** Copy mode's box: the NAME of the vault secret to copy (B28). `error` only once submitted. */
function CopySourceInput({
  sourceName,
  error,
  onChange,
}: {
  sourceName: string;
  error: string | undefined;
  onChange: (name: string) => void;
}) {
  return (
    <SkyInput
      label={COPY_SOURCE_LABEL}
      value={sourceName}
      hint={COPY_SOURCE_HINT}
      testId="replace-secret-copy-source"
      autoComplete="off"
      ref={withoutSpellcheck}
      validationState={error ? "error" : "none"}
      validationMessage={error}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

/** The new value's box and the warning that it is never shown again. `error` only once submitted. */
function NewValueInput({
  label,
  hint,
  value,
  multiline,
  error,
  onChange,
}: {
  label: string;
  hint: string | undefined;
  value: string;
  multiline: boolean;
  error: string | undefined;
  onChange: (value: string) => void;
}) {
  return (
    <>
      <SkyTextarea
        label={label}
        value={value}
        rows={multiline ? 6 : 2}
        hint={hint}
        testId="replace-secret-value"
        autoComplete="off"
        spellCheck={false}
        validationState={error ? "error" : "none"}
        validationMessage={error}
        onChange={(event) => onChange(event.target.value)}
      />
      <SkyMessageBar
        intent="warning"
        layout="multiline"
        title="The value is never shown again"
        message="After saving, this field reads Stored. Keep your own copy."
      />
    </>
  );
}

/** What blocks the save: no source name or a malformed one in copy mode, else no value. */
function replaceSecretError(
  copying: boolean,
  trimmedSource: string,
  value: string,
): string | undefined {
  if (!copying) return value.trim() ? undefined : "Paste the new value.";
  if (!trimmedSource) return "Enter the name of the secret to copy.";
  return COPY_SOURCE_NAME_PATTERN.test(trimmedSource) ? undefined : COPY_SOURCE_NAME_RULE;
}

/**
 * Replacing a credential (canvas 08, "Replace secret").
 *
 * The value is entered once and never read back. The dialog says so before the
 * user commits, because "keep your own copy" is advice that only helps in
 * advance, and it states the write order the server actually uses: the vault is
 * written first, and only when the vault confirms does the instance point at the
 * new version (SEC-2, `server/secrets/writeOrder.ts`).
 *
 * The value is HELD for the save rather than written here, so a user who changes
 * their mind before saving has changed nothing at all.
 *
 * B28 (copy mode): when the caller passes `onStageCopy` — the destination screen
 * does so only on its platform-admin path, the one role that can reach the
 * platform route — a switch swaps the value box for the NAME of a secret already
 * in the deployment's vault. The server copies that secret's value into this
 * instance's own secret on save; the value never reaches the browser.
 */
export function ReplaceSecretDialog({
  open,
  title,
  label,
  hint,
  multiline = true,
  onOpenChange,
  onStage,
  onStageCopy,
}: {
  open: boolean;
  title: string;
  label: string;
  hint?: string;
  multiline?: boolean;
  onOpenChange: (open: boolean) => void;
  onStage: (value: string) => void;
  /** Offers copy mode; called with the source secret's name (B28, platform admins only). */
  onStageCopy?: (name: string) => void;
}) {
  const [value, setValue] = useState("");
  const [copyMode, setCopyMode] = useState(false);
  const [sourceName, setSourceName] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Reset on BOTH edges, during render rather than in an effect. On opening,
  // because a credential from a previous attempt must never be on screen for a
  // frame; on CLOSING, because plaintext the user typed and then cancelled has
  // no reason to sit in component state until the next opening.
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    setValue("");
    setCopyMode(false);
    setSourceName("");
    setSubmitted(false);
  }

  const copying = copyMode && onStageCopy !== undefined;
  const trimmedSource = sourceName.trim();
  const error = replaceSecretError(copying, trimmedSource, value);

  return (
    <SkyDialog
      open={open}
      onOpenChange={onOpenChange}
      size="medium"
      testId="replace-secret-dialog"
      title={title}
      description="The new value is stored securely first. Only then does this instance switch to it."
      primaryAction={{
        label: "Replace on save",
        onClick: () => {
          setSubmitted(true);
          if (error) return;
          if (copying && onStageCopy) onStageCopy(trimmedSource);
          else onStage(value);
          onOpenChange(false);
        },
      }}
      secondaryAction={{ label: "Cancel", onClick: () => onOpenChange(false) }}
    >
      <div className="space-y-4">
        {onStageCopy ? (
          <SkySwitch
            label={COPY_SWITCH_LABEL}
            checked={copyMode}
            testId="replace-secret-copy-switch"
            onCheckedChange={(checked) => {
              setCopyMode(checked);
              // Nothing typed in the other mode survives the switch.
              setValue("");
              setSourceName("");
              setSubmitted(false);
            }}
          />
        ) : null}
        {copying ? (
          <CopySourceInput
            sourceName={sourceName}
            error={submitted ? error : undefined}
            onChange={setSourceName}
          />
        ) : (
          <NewValueInput
            label={label}
            hint={hint}
            value={value}
            multiline={multiline}
            error={submitted ? error : undefined}
            onChange={setValue}
          />
        )}
      </div>
    </SkyDialog>
  );
}
